import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';

import { fromEvent, Observable } from 'rxjs';
import { delay, filter, mapTo, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';

import { ShClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { NavigationLinkDirective } from '../../directives/navigation-link/navigation-link.directive';
import { ArrowNavigationHelper } from '../../helpers/arrow-navigation.helper';
import {
    fixElementBottomToParentBottom,
    fixElementBottomToParentTop,
    fixElementLeftToParentLeft,
    fixElementLeftToParentRight,
    fixElementRightToParentLeft,
    fixElementRightToParentRight,
    fixElementTopToParentBottom,
    fixElementTopToParentTop
} from '../../helpers/dom-positioning.helper';
import { ShIconModule } from '../icon/icon.module';
import { ShBaseActionItemComponent } from './_base-action-item.component';
import { ShDroppanelItemComponent } from './droppanel-item.component';
import { ActionItem, OpenDirection } from './entities/action-item.entity';
import { ShChildIndexDirective } from '../../directives/child-index/child-index';

export * from './entities/action-item.entity';

@Component({
    standalone: true,
    selector: 'sh-action-item',
    templateUrl: './action-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ShIconModule,
        ShDroppanelItemComponent,
        NavigationLinkDirective,
        ShClickOutsideDirective,
        ShChildIndexDirective
    ]
})
export class ShActionItemComponent extends ShBaseActionItemComponent implements OnInit, OnDestroy, AfterViewChecked {
    private readonly OPTION_QUERY: string = '.sh-droppanel__list-action:not(.-sh-disabled)';
    @ViewChild('droppanel') droppanel?: ElementRef<HTMLElement>;
    @ViewChild('droppanelToggle') droppanelToggle?: ElementRef<HTMLElement>;

    @ViewChild('linkItem') linkItemElement?: ElementRef<HTMLElement>;
    @ViewChild('buttonItem') buttonItemElement?: ElementRef<HTMLElement>;

    @Input() disableArrowNavigation = false;

    public arrowNavigation: ArrowNavigationHelper;
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        // To prevent interfering with other components
        if (!this.disableArrowNavigation) {
            this.arrowNavigation.handleKeyDown({
                event,
                optionContainerElement: this.droppanel,
                toggleContainer: this.droppanelToggle,
                closeDropdown: () => this.setDropdownClosed()
            });
        }
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initArrowNavigation();
        this.initOpenListener();
        this.initAdjustDroppanelPositionSubscriber();
    }

    ngAfterViewChecked(): void {
        if (!this.disableArrowNavigation) {
            this.arrowNavigation.manageFocus(this.isDropdownOpen$.value, this.droppanel, null);
        }
    }

    private initArrowNavigation(): void {
        if (!this.disableArrowNavigation) {
            this.arrowNavigation = new ArrowNavigationHelper(
                this.cdr,
                this.isDropdownOpen$,
                this.destroy$,
                this.OPTION_QUERY,
                true
            );
        }
    }

    private initOpenListener(): void {
        if (!this.disableArrowNavigation) {
            this.isDropdownOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
                if (!isOpen) {
                    this.arrowNavigation.resetFocus(0);
                }
            });
        }
    }

    private initAdjustDroppanelPositionSubscriber(): void {
        if (!this.inOverflownContainer) {
            this.isDropdownOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
                // Set class moment the dropdown is opened/closed
                this.setDropdownOpenClass$.next(isOpen);
            });
            return;
        }

        // capture: true needed for capturing scroll event inside overflow containers
        const didScroll$: Observable<boolean> = fromEvent(window, 'scroll', { capture: true }).pipe(
            mapTo(true),
            startWith(true)
        );

        this.isDropdownOpen$
            .pipe(
                filter(isOpen => !isOpen),
                takeUntil(this.destroy$)
            )
            .subscribe(isOpen => {
                // Unset class moment the dropdown closed
                this.setDropdownOpenClass$.next(isOpen);
            });

        this.isDropdownOpen$
            .pipe(
                filter(isOpen => isOpen),
                delay(0),
                switchMap(() => didScroll$),
                withLatestFrom(this.item$),
                takeUntil(this.destroy$)
            )
            .subscribe(([_, item]) => {
                if (this.droppanel) {
                    // Place dropdown out of view to prevent flickering
                    this.droppanelNativeElement.style.top = window.innerHeight + 'px';
                    this.droppanelNativeElement.style.visibility = 'visible';
                    this.setDropdownOpenClass$.next(this.isDropdownOpen$.value);

                    // Timeout needed so the droppanel has a defined height by time this triggers
                    setTimeout(() => {
                        this.positionDropdown(item);
                    });
                }
            });
    }

    private positionDropdown(item: ActionItem): void {
        const boundingClientRectOfToggle: DOMRect | undefined =
            this.droppanelToggle?.nativeElement.getBoundingClientRect();

        switch (item.openDirection) {
            case OpenDirection.AboveLeft:
                fixElementRightToParentRight(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementBottomToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.AboveRight:
                fixElementLeftToParentLeft(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementBottomToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.BelowLeft:
                fixElementTopToParentBottom(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementRightToParentRight(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.BelowRight:
                fixElementTopToParentBottom(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementLeftToParentLeft(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.LeftDown:
                fixElementTopToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementRightToParentLeft(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.LeftOverlap:
                fixElementRightToParentRight(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementTopToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.LeftUp:
                fixElementRightToParentLeft(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementBottomToParentBottom(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.RightDown:
                fixElementTopToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementLeftToParentRight(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.RightOverlap:
                fixElementTopToParentTop(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementLeftToParentLeft(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            case OpenDirection.RightUp:
                fixElementLeftToParentRight(this.droppanelNativeElement, boundingClientRectOfToggle);
                fixElementBottomToParentBottom(this.droppanelNativeElement, boundingClientRectOfToggle);
                break;
            default:
                break;
        }
    }

    private get droppanelNativeElement(): HTMLElement {
        return this.droppanel!.nativeElement;
    }

    public get focusIndex(): number {
        return this.arrowNavigation?.focusIndex ?? -1;
    }

    public onHover(event: MouseEvent): void {
        if (!this.disableArrowNavigation) {
            this.arrowNavigation.onHover(event.target as HTMLElement, this.droppanel);
        }
    }
}
